// =============================================================================
// FEEDBACK PAGE — admin-side viewer for user-submitted feedback
//
// Same component renders on both super-admin (no tenantId → all
// tenants) and tenant-admin (tenantId scoped). Top status pills
// switch between New / Read / Archived; cards show comment, rating,
// reporter identity, and tenant tag (super admin only). Each row has
// a Mark read / Archive / Reopen action.
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { MessageSquare, Star, Mail, User, Globe2, RefreshCw, CheckCircle2, Archive, RotateCcw, Inbox } from 'lucide-react';
import { Spinner } from '@/components/shared/UIComponents';
import {
  fetchTenantFeedback,
  updateFeedbackStatus,
  type FeedbackItem,
  type FeedbackListResult,
} from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';

export interface FeedbackPageProps {
  /** Scope: tenant admin passes their tenantId. Super admin omits it
   *  to fetch across all tenants. */
  tenantId?: string;
  /** Optional tenant-name resolver for the super-admin view so each
   *  row shows which tenant the feedback came from. */
  tenantNameById?: Record<string, string>;
}

type StatusFilter = 'new' | 'read' | 'archived' | 'all';

export function FeedbackPage({ tenantId, tenantNameById }: FeedbackPageProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('new');
  const [result,       setResult]       = useState<FeedbackListResult | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState<string | null>(null);
  const [actingId,     setActingId]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Always fetch with status='all' so the stat pills can show
      // accurate counts; filter client-side for the visible list.
      const r = await fetchTenantFeedback({ tenantId, status: 'all', limit: 200 });
      setResult(r);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (item: FeedbackItem, action: 'mark_read' | 'archive' | 'reopen') => {
    setActingId(item.id);
    try {
      await updateFeedbackStatus(item.id, action);
      // Refresh — cheap, small payload.
      await load();
    } catch (e) {
      alert(`Failed: ${errorMessage(e)}`);
    } finally {
      setActingId(null);
    }
  };

  const visible = (result?.items || []).filter(i =>
    statusFilter === 'all' ? true : i.status === statusFilter
  );

  const stats = result?.stats || { new: 0, read: 0, archived: 0 };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <MessageSquare size={22} className="text-brand-600" />
          <div>
            <h1 className="text-xl font-bold text-surface-900">Feedback</h1>
            <p className="text-sm text-surface-500">
              {tenantId
                ? 'User-submitted feedback for this tenant.'
                : 'User-submitted feedback across every tenant.'}
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-surface-100 hover:bg-surface-200 text-surface-900 inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      {/* Status filter pills */}
      <div className="flex items-center gap-2 mb-4">
        {([
          ['new',      'New',      stats.new],
          ['read',     'Read',     stats.read],
          ['archived', 'Archived', stats.archived],
          ['all',      'All',      stats.new + stats.read + stats.archived],
        ] as Array<[StatusFilter, string, number]>).map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              statusFilter === key
                ? 'bg-brand-600 text-white'
                : 'bg-surface-100 text-surface-700 hover:bg-surface-200'
            }`}
          >
            {label} <span className="opacity-70">({count})</span>
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-xs text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="md" />
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-12 glass-panel">
          <Inbox className="w-10 h-10 mx-auto mb-3 text-surface-400" />
          <p className="text-sm font-medium text-surface-700">No feedback in this view</p>
          <p className="text-xs text-surface-500 mt-1">
            {statusFilter === 'new'
              ? 'New feedback will show up here as users submit it.'
              : 'Try switching the filter above.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map(item => (
            <div key={item.id} className="glass-panel p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  {/* Top row: rating + identity + timestamp */}
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {item.rating != null && (
                      <span className="inline-flex items-center gap-0.5 text-amber-500">
                        {[1, 2, 3, 4, 5].map(n => (
                          <Star key={n} size={12} className={n <= item.rating! ? 'fill-amber-400 text-amber-400' : 'text-surface-300'} />
                        ))}
                      </span>
                    )}
                    {item.is_anonymous && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
                        Guest
                      </span>
                    )}
                    {item.status === 'new' && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-100 text-brand-700">
                        New
                      </span>
                    )}
                    {item.status === 'archived' && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-surface-200 text-surface-600">
                        Archived
                      </span>
                    )}
                    {!tenantId && tenantNameById?.[item.tenant_id] && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-surface-100 text-surface-700 inline-flex items-center gap-1">
                        <Globe2 size={9} />
                        {tenantNameById[item.tenant_id]}
                      </span>
                    )}
                    <span className="ml-auto text-[11px] text-surface-500">
                      {new Date(item.created_at).toLocaleString()}
                    </span>
                  </div>

                  {/* Comment body */}
                  <p className="text-sm text-surface-900 whitespace-pre-wrap leading-relaxed">
                    {item.comment}
                  </p>

                  {/* Identity footer */}
                  <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px] text-surface-500">
                    {item.user_name && (
                      <span className="inline-flex items-center gap-1">
                        <User size={11} />
                        {item.user_name}
                      </span>
                    )}
                    {item.user_email && (
                      <a href={`mailto:${item.user_email}`} className="inline-flex items-center gap-1 hover:text-brand-600">
                        <Mail size={11} />
                        {item.user_email}
                      </a>
                    )}
                    {item.page_url && (
                      <span className="font-mono text-surface-400 truncate" title={item.page_url}>
                        from {item.page_url}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-1.5 shrink-0">
                  {item.status === 'new' && (
                    <button
                      onClick={() => handleAction(item, 'mark_read')}
                      disabled={actingId === item.id}
                      className="px-2 py-1 rounded text-[11px] font-medium bg-surface-100 hover:bg-surface-200 text-surface-700 inline-flex items-center gap-1 disabled:opacity-50"
                      title="Mark as read"
                    >
                      <CheckCircle2 size={11} />
                      Read
                    </button>
                  )}
                  {item.status !== 'archived' && (
                    <button
                      onClick={() => handleAction(item, 'archive')}
                      disabled={actingId === item.id}
                      className="px-2 py-1 rounded text-[11px] font-medium bg-surface-100 hover:bg-surface-200 text-surface-700 inline-flex items-center gap-1 disabled:opacity-50"
                      title="Archive"
                    >
                      <Archive size={11} />
                      Archive
                    </button>
                  )}
                  {item.status === 'archived' && (
                    <button
                      onClick={() => handleAction(item, 'reopen')}
                      disabled={actingId === item.id}
                      className="px-2 py-1 rounded text-[11px] font-medium bg-surface-100 hover:bg-surface-200 text-surface-700 inline-flex items-center gap-1 disabled:opacity-50"
                      title="Reopen"
                    >
                      <RotateCcw size={11} />
                      Reopen
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
